import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Paperclip, X, AlertCircle } from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import { supportAPI } from '../../services/supportAPI';
import { stationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useUser } from '../../context/UserContext';

const TICKET_CATEGORIES = [
  'Booking Issue',
  'Payment Issue',
  'Refund Request',
  'Room Allocation',
  'Room Cleanliness',
  'AC / Water / Electricity',
  'Staff Complaint',
  'Lost & Found',
  'Cancellation',
  'Technical Issue',
  'Other'
];

const CreateTicket = () => {
  const { user, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    station: '',
    bookingId: '',
    customerName: user?.name || '',
    email: user?.email || '',
    phone: user?.mobile || '',
    description: ''
  });

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const res = await stationsAPI.getAll();
        setStations(res.data.stations);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStations();
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
        const newFiles = Array.from(e.target.files);
        if (files.length + newFiles.length > 3) {
            toast.error('Maximum 3 files allowed');
            return;
        }
        
        const validFiles = newFiles.filter(f => {
            const ext = f.name.toLowerCase().split('.').pop();
            const valid = ['jpg', 'jpeg', 'png', 'pdf'].includes(ext);
            if (!valid) toast.error(`${f.name} is not a supported file type`);
            return valid && f.size <= 5 * 1024 * 1024;
        });

        if (validFiles.length < newFiles.length) {
            toast.error('Some files were skipped (invalid type or > 5MB)');
        }
        
        setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index) => {
      setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      Object.keys(formData).forEach(key => {
          if (formData[key]) payload.append(key, formData[key]);
      });
      
      files.forEach(file => payload.append('attachments', file));

      const res = await supportAPI.createTicket(payload);
      toast.success('Ticket created successfully!');
      navigate(`/support/ticket/${res.data.ticket.ticketNumber}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <SEO title="Raise a Ticket | Retiring Room" description="Create a support ticket for assistance." />
      
      <div className="min-h-screen bg-midnight-50 py-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="card p-8">
            <h1 className="text-3xl font-display font-bold text-midnight-900 mb-6">Raise a Support Ticket</h1>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-midnight-700 mb-1">Category *</label>
                    <select 
                        name="category" 
                        required 
                        value={formData.category} 
                        onChange={handleChange}
                        className="input-field"
                    >
                        <option value="">Select Category</option>
                        {TICKET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-midnight-700 mb-1">Subject *</label>
                    <input 
                        type="text" 
                        name="subject" 
                        required 
                        value={formData.subject} 
                        onChange={handleChange}
                        placeholder="Brief summary of issue"
                        className="input-field"
                    />
                  </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-midnight-700 mb-1">Station (Optional)</label>
                    <select 
                        name="station" 
                        value={formData.station} 
                        onChange={handleChange}
                        className="input-field"
                    >
                        <option value="">Select Station</option>
                        {stations.map(s => <option key={s._id} value={s._id}>{s.name} ({s.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-midnight-700 mb-1">Booking ID / PNR (Optional)</label>
                    <input 
                        type="text" 
                        name="bookingId" 
                        value={formData.bookingId} 
                        onChange={handleChange}
                        placeholder="e.g. 1234567890"
                        className="input-field"
                    />
                  </div>
              </div>

              <div className="border-t border-midnight-100 pt-6">
                  <h3 className="text-lg font-semibold text-midnight-900 mb-4">Contact Information</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-midnight-700 mb-1">Name *</label>
                        <input 
                            type="text" 
                            name="customerName" 
                            required 
                            value={formData.customerName} 
                            onChange={handleChange}
                            className="input-field"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-midnight-700 mb-1">Email *</label>
                        <input 
                            type="email" 
                            name="email" 
                            required 
                            value={formData.email} 
                            onChange={handleChange}
                            className="input-field"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-midnight-700 mb-1">Phone *</label>
                        <input 
                            type="text" 
                            name="phone" 
                            required 
                            value={formData.phone} 
                            onChange={handleChange}
                            className="input-field"
                        />
                      </div>
                  </div>
              </div>

              <div className="border-t border-midnight-100 pt-6">
                  <label className="block text-sm font-medium text-midnight-700 mb-1">Detailed Description *</label>
                  <textarea 
                      name="description" 
                      required 
                      value={formData.description} 
                      onChange={handleChange}
                      rows="5"
                      placeholder="Please provide as much detail as possible..."
                      className="input-field resize-none"
                  ></textarea>
              </div>

              <div>
                  <label className="block text-sm font-medium text-midnight-700 mb-2">Attachments (Optional, max 3 files, 5MB each)</label>
                  <div className="flex items-center gap-4">
                      <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                          <Paperclip className="w-4 h-4" />
                          <span>Attach Files</span>
                          <input 
                              type="file" 
                              multiple 
                              accept=".jpg,.jpeg,.png,.pdf" 
                              className="hidden" 
                              onChange={handleFileChange}
                          />
                      </label>
                      <span className="text-sm text-midnight-500">JPG, PNG, or PDF</span>
                  </div>
                  
                  {files.length > 0 && (
                      <div className="mt-4 flex gap-2 flex-wrap">
                          {files.map((file, i) => (
                              <div key={i} className="flex items-center gap-2 bg-midnight-50 border border-midnight-200 px-3 py-1.5 rounded-lg text-sm">
                                  <span className="truncate max-w-[150px]">{file.name}</span>
                                  <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700">
                                      <X className="w-4 h-4" />
                                  </button>
                              </div>
                          ))}
                      </div>
                  )}
              </div>

              <div className="pt-4 border-t border-midnight-100 flex justify-end gap-4">
                  <button type="button" onClick={() => navigate('/help')} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2">
                      {loading ? 'Submitting...' : <><Send className="w-4 h-4" /> Submit Ticket</>}
                  </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CreateTicket;
